import { NetworkStatus, useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { Box, Icon, IconSize, SideSheetBody, SideSheetContentCol, Text } from '@bechtel/gds';
import {
  OperatorType,
  SearchInput,
  SearchUserAccountProjectResult,
  SortType,
  UiTextEnum,
  UpdateUserAccountProjectInput,
  UpdateUserAccountProjectResult,
  UpdateUserDefaultProjectInput,
  UpdateUserDefaultProjectResult,
  UserAccountProject,
} from '@bechtel/sr-types';
import { filter, get, map } from 'lodash';
import { FC, useDeferredValue, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  AppSpinner,
  EMULATED_USER_ID,
  ErrorBoundary,
  ErrorRetry,
  MultiLingualText,
  SearchField,
  USER_PROJECT_ID,
  useAppUser,
  useLanguage,
} from '~/components';
import { GET_USER_ACCOUNT_PROJECTS, UPDATE_USER_ACCOUNT_PROJECT, UPDATE_USER_DEFAULT_PROJECT } from '~/gql';
import { useOutsideClick } from '~/hooks';
//@ts-ignore ignore image import
import SetrouteLogo from '~/images/setroute-logo-full.svg';
import {
  DrawerBackdropStyled,
  DrawerOuterStyled,
  ProjectImageStyled,
  ProjectTileStyled,
  SubContainerStyled,
} from './styled';

interface ProjectTileProp {
  item: UserAccountProject;
  selected?: boolean;
  onSelectProject: () => void;
  onSetFavorite: (item: UserAccountProject, isFavorite: boolean) => void;
}

interface ProjectListingProps {
  close: () => void;
}

export const ProjectTile: FC<ProjectTileProp> = ({ item, selected, onSelectProject, onSetFavorite }) => {
  const { project } = item;

  return (
    <ProjectTileStyled data-testid={`project-list-${item.id}`} onClick={onSelectProject}>
      <ProjectImageStyled>
        <img src={project?.logo ? project?.logo : SetrouteLogo} />
      </ProjectImageStyled>
      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} justifyContent={{ sm: 'center' }}>
        <Box justifyContent={{ sm: 'space-between' }}>
          <Box flexDirection={{ sm: 'column' }}>
            <Box justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }}>
              <Text size="300" weight="600" color="primary.900">
                {project?.name}
              </Text>
              <Box pl={{ sm: '300' }}>
                {selected && <Icon icon="check" iconStyle="regular" size={IconSize.SMALL} color="primary.600" />}
              </Box>
            </Box>
            <Box pt={{ sm: '200' }}>
              <Text size="200" color="grey.600">
                {project?.jobNumber}
                {project?.subJobNumber && `-${project?.subJobNumber}`}
              </Text>
            </Box>
          </Box>
          <Box
            pl={{ sm: '300' }}
            onClick={(e) => {
              e.stopPropagation();
              onSetFavorite(item, !item.isFavorite);
            }}
            flexShrink={{ sm: 1 }}
          >
            {/* Ternary not working here as Icon not flipping the iconStyle property */}
            {item.isFavorite && <Icon icon="star" iconStyle="solid" size={IconSize.MEDIUM} fillColor="primary.500" />}
            {!item.isFavorite && <Icon icon="star" iconStyle="regular" size={IconSize.MEDIUM} fillColor="grey.400" />}
          </Box>
        </Box>
      </Box>
    </ProjectTileStyled>
  );
};

export const ProjectListing: FC<ProjectListingProps> = ({ close }) => {
  const navigate = useNavigate();
  const { userAccount } = useAppUser();
  const client = useApolloClient();
  const [searchValue, setSearchValue] = useState('');
  const debounceSearchValue = useDeferredValue(searchValue?.trim());
  const { getMultiLingualTextByKey } = useLanguage();
  const [searchUserProjects, { loading, error, data, refetch, networkStatus }] = useLazyQuery<
    { searchUserAccountProjects: SearchUserAccountProjectResult },
    {
      searchInput: SearchInput;
    }
  >(GET_USER_ACCOUNT_PROJECTS, {
    variables: {
      searchInput: {
        filterInput: {
          and: [
            {
              field: 'userAccountId',
              value: userAccount?.id,
              operator: OperatorType.EQUAL,
            },
            {
              field: 'project.name',
              value: debounceSearchValue,
              operator: OperatorType.LIKE,
            },
            {
              field: 'project.isActive',
              value: 'true',
              operator: OperatorType.EQUAL,
            },
          ],
        },
        sortInput: [
          { field: 'isFavorite', sort: SortType.DESC },
          { field: 'project.name', sort: SortType.ASC },
        ],
      },
    },
    fetchPolicy: 'network-only',
  });

  const userAccountProjectList = get(data?.searchUserAccountProjects, 'items', []);
  const favoriteProjectList = filter(
    userAccountProjectList,
    (userAccountProject: UserAccountProject) => userAccountProject.isFavorite === true
  );
  const allProjectList = filter(
    userAccountProjectList,
    (userAccountProject: UserAccountProject) => !userAccountProject.isFavorite
  );
  const userProjectId = window.sessionStorage.getItem(USER_PROJECT_ID);

  const [updateUserDefaultProject] = useMutation<
    { updateUserDefaultProject: UpdateUserDefaultProjectResult },
    { updateUserDefaultProjectInput: UpdateUserDefaultProjectInput }
  >(UPDATE_USER_DEFAULT_PROJECT, {
    onCompleted: async (data) => {
      window.sessionStorage.setItem(EMULATED_USER_ID, '');
      window.sessionStorage.setItem(
        USER_PROJECT_ID,
        data?.updateUserDefaultProject?.userAccount?.defaultProjectId
          ? data?.updateUserDefaultProject?.userAccount?.defaultProjectId
          : ''
      );
      navigate('/', { replace: true });
      close();
      await client.resetStore();
    },
  });

  const [updateUserAccountProject] = useMutation<
    { updateUserAccountProject: UpdateUserAccountProjectResult },
    { updateUserAccountProjectInput: UpdateUserAccountProjectInput }
  >(UPDATE_USER_ACCOUNT_PROJECT);

  useEffect(() => {
    if (debounceSearchValue.length > 0 && debounceSearchValue.length < 3) return;
    searchUserProjects();
  }, [debounceSearchValue, searchUserProjects]);

  const handleSetFavorite = async (item: UserAccountProject, isFavorite: boolean) => {
    await updateUserAccountProject({
      variables: {
        updateUserAccountProjectInput: {
          id: item.id,
          projectId: item.projectId,
          userAccountId: item.userAccountId,
          isFavorite: isFavorite,
        },
      },
    });
  };

  const ref = useRef();
  useOutsideClick(ref, () => {
    close();
  });

  return (
    <DrawerBackdropStyled onClick={close} ref={ref}>
      <DrawerOuterStyled
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <SideSheetBody>
          <ErrorBoundary>
            <SideSheetContentCol>
              <Box p={{ sm: '450' }}>
                <SearchField
                  placeholder={getMultiLingualTextByKey(UiTextEnum.SEARCH)}
                  onChange={(event) => setSearchValue(event.target.value)}
                />
              </Box>
              <Box flexDirection={{ sm: 'column' }}>
                {(loading || networkStatus === NetworkStatus.refetch) && (
                  <Box css="height: 100%;" data-testid="loading">
                    <Box
                      flexGrow={{ sm: 1 }}
                      alignItems={{ sm: 'center' }}
                      flexDirection={{ sm: 'column' }}
                      justifyContent={{ sm: 'center' }}
                    >
                      <AppSpinner size={IconSize.MEDIUM} />
                    </Box>
                  </Box>
                )}
                {!loading && error && <ErrorRetry refetch={refetch} />}
                {!loading && !error && userAccountProjectList && userAccountProjectList.length === 0 && (
                  <Box p={{ sm: '450' }} justifyContent={{ sm: 'center' }}>
                    <MultiLingualText>No project found</MultiLingualText>
                  </Box>
                )}
                {favoriteProjectList && favoriteProjectList.length > 0 && (
                  <>
                    <SubContainerStyled>
                      <MultiLingualText color="grey.600" size="300">
                        Favorite
                      </MultiLingualText>
                    </SubContainerStyled>
                    {map(favoriteProjectList, (item) => (
                      <ProjectTile
                        item={item}
                        key={item.project?.id}
                        selected={item.project?.id === userProjectId}
                        onSelectProject={async () => {
                          if (item.project?.id) {
                            await updateUserDefaultProject({
                              variables: {
                                updateUserDefaultProjectInput: { projectId: item.project?.id },
                              },
                            });
                          }
                        }}
                        onSetFavorite={handleSetFavorite}
                      />
                    ))}
                  </>
                )}
                {allProjectList && allProjectList.length > 0 && (
                  <>
                    <SubContainerStyled>
                      <MultiLingualText color="grey.600" size="300">
                        All Projects
                      </MultiLingualText>
                    </SubContainerStyled>
                    {map(allProjectList, (item) => (
                      <ProjectTile
                        item={item}
                        key={item.project?.id}
                        selected={item.project?.id === userProjectId}
                        onSelectProject={async () => {
                          if (item.project?.id) {
                            await updateUserDefaultProject({
                              variables: {
                                updateUserDefaultProjectInput: { projectId: item.project?.id },
                              },
                            });
                          }
                        }}
                        onSetFavorite={handleSetFavorite}
                      />
                    ))}
                  </>
                )}
              </Box>
            </SideSheetContentCol>
          </ErrorBoundary>
        </SideSheetBody>
      </DrawerOuterStyled>
    </DrawerBackdropStyled>
  );
};
